/**
 * Breadcrumb Components
 * Navigation breadcrumb trail
 */

import React, { forwardRef, Children, cloneElement, isValidElement } from 'react';
import { Box } from '../primitives/Box';
import { Link } from '../primitives/Link';

/**
 * Breadcrumb Component
 * 
 * @example
 * <Breadcrumb separator="/">
 *   <BreadcrumbItem>
 *     <BreadcrumbLink href="/">Home</BreadcrumbLink>
 *   </BreadcrumbItem>
 *   <BreadcrumbItem isCurrentPage>
 *     <BreadcrumbLink>Current</BreadcrumbLink>
 *   </BreadcrumbItem>
 * </Breadcrumb>
 */
export const Breadcrumb = forwardRef(({
  separator = '/',
  spacing = 2,
  children,
  ...props
}, ref) => {
  const validChildren = Children.toArray(children).filter(isValidElement);
  const count = validChildren.length;

  const clonedChildren = validChildren.map((child, index) => {
    return cloneElement(child, {
      separator: child.props.separator || separator,
      spacing,
      isLastChild: index === count - 1,
    });
  });

  return (
    <Box
      ref={ref}
      as="nav"
      aria-label="breadcrumb"
      {...props}
    >
      <Box
        as="ol"
        display="flex"
        alignItems="center"
        listStyleType="none"
        m={0}
        p={0}
      >
        {clonedChildren}
      </Box>
    </Box>
  );
});

Breadcrumb.displayName = 'Breadcrumb';

/**
 * BreadcrumbItem Component
 */
export const BreadcrumbItem = forwardRef(({
  isCurrentPage = false,
  isLastChild = false,
  separator,
  spacing = 2,
  children,
  ...props
}, ref) => {
  // Clone children to pass isCurrentPage
  const clonedChildren = Children.map(children, (child) => {
    if (isValidElement(child) && child.type?.displayName === 'BreadcrumbLink') {
      return cloneElement(child, {
        isCurrentPage,
      });
    }
    return child;
  });

  return (
    <Box
      ref={ref}
      as="li"
      display="inline-flex"
      alignItems="center"
      {...props}
    >
      {clonedChildren}
      {!isLastChild && (
        <Box
          as="span"
          mx={spacing}
          color="gray.500"
          role="presentation"
        >
          {separator}
        </Box>
      )}
    </Box>
  );
});

BreadcrumbItem.displayName = 'BreadcrumbItem';

/**
 * BreadcrumbLink Component
 */
export const BreadcrumbLink = forwardRef(({
  isCurrentPage = false,
  href,
  children,
  ...props
}, ref) => {
  if (isCurrentPage) {
    return (
      <Box
        ref={ref}
        as="span"
        aria-current="page"
        fontWeight="semibold"
        color="gray.800"
        {...props}
      >
        {children}
      </Box>
    );
  }

  return (
    <Link
      ref={ref}
      href={href}
      color="gray.600"
      _hover={{ color: 'brand.500' }}
      {...props}
    >
      {children}
    </Link>
  );
});

BreadcrumbLink.displayName = 'BreadcrumbLink';

/**
 * BreadcrumbSeparator Component
 */
export const BreadcrumbSeparator = forwardRef(({
  children = '/',
  ...props
}, ref) => {
  return (
    <Box
      ref={ref}
      as="span"
      mx={2}
      color="gray.500"
      role="presentation"
      {...props}
    >
      {children}
    </Box>
  );
});

BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';

export default Breadcrumb;
